import { formatDate, MOMENT_FORMATS, parseEventDateTime } from '../../../utils/dateFormat';
import {
  formatAriaEventLocation,
  formatAriaEventPrice,
  getAriaMessageDataEnvelope,
} from '../../AriaHandsoff/ariaMessageTypes';

/** Matches member-app Chat `message.data.messageType`. */
export const SHARED_EVENT_MESSAGE_TYPE = 'shared_event';

const toTrimmed = (value) => {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
};

const getSharedEventEnvelope = (messageOrData = {}) => {
  if (messageOrData?.messageType != null && typeof messageOrData === 'object') {
    return messageOrData;
  }
  return getAriaMessageDataEnvelope(messageOrData);
};

export const getSharedEventMessageType = (messageOrData = {}) => {
  const envelope = getSharedEventEnvelope(messageOrData);
  return toTrimmed(envelope?.messageType)?.toLowerCase() ?? null;
};

export const isSharedEventMessage = (messageOrData = {}) =>
  getSharedEventMessageType(messageOrData) === SHARED_EVENT_MESSAGE_TYPE ||
  messageOrData?.sharedEventPayload?.messageType === SHARED_EVENT_MESSAGE_TYPE;

/** Member-app SharedEventCard date label: "MMM D". */
export const formatSharedEventDateLabel = (eventDate) => {
  const date = parseEventDateTime(eventDate, null);
  return date ? formatDate(date, MOMENT_FORMATS.DATE_SHORT) : null;
};

/** Member-app SharedEventCard time label: "h:mm A". */
export const formatSharedEventTimeLabel = (eventDate, startTime) => {
  if (!toTrimmed(eventDate) || !toTrimmed(startTime)) return null;
  const date = parseEventDateTime(eventDate, startTime);
  return date ? formatDate(date, MOMENT_FORMATS.TIME) : null;
};

/**
 * Normalize Stream `message.data` when messageType === shared_event.
 * Shape mirrors safespace-frontend SharedEventCard.getSharedEventFromMessage.
 */
export const normalizeSharedEventMessage = (messageOrData = {}) => {
  if (messageOrData?.sharedEventPayload?.messageType === SHARED_EVENT_MESSAGE_TYPE) {
    return messageOrData.sharedEventPayload;
  }

  const envelope = getSharedEventEnvelope(messageOrData);
  if (!envelope || getSharedEventMessageType(envelope) !== SHARED_EVENT_MESSAGE_TYPE) {
    return null;
  }

  const eventId = toTrimmed(envelope.eventId) ?? toTrimmed(envelope.id);
  if (!eventId) return null;

  const seatsRaw = envelope.seatsRemaining;
  const seatsRemaining = seatsRaw == null || seatsRaw === '' ? null : Number(seatsRaw);
  const bookedUsers = Array.isArray(envelope.bookedUsers) ? envelope.bookedUsers : [];
  const deletedAt = toTrimmed(envelope.deletedAt);
  const dateLabel = formatSharedEventDateLabel(envelope.eventDate);
  const timeLabel = formatSharedEventTimeLabel(envelope.eventDate, envelope.startTime);
  const locationLine = formatAriaEventLocation(envelope);
  const detailLine = [dateLabel, locationLine, timeLabel].filter(Boolean).join(' · ') || null;
  const priceLine = formatAriaEventPrice(envelope.price, envelope.currency ?? 'SS');
  const peopleLabel =
    bookedUsers.length > 0
      ? `${bookedUsers.length} ${bookedUsers.length === 1 ? 'person' : 'people'} at your table`
      : null;
  const subLine = [priceLine, peopleLabel].filter(Boolean).join(' · ') || null;

  return {
    messageType: SHARED_EVENT_MESSAGE_TYPE,
    eventId,
    id: eventId,
    title: toTrimmed(envelope.title) || 'Event',
    coverImageUrl: toTrimmed(envelope.coverImageUrl),
    seatsRemaining: Number.isFinite(seatsRemaining) ? seatsRemaining : null,
    eventDate: toTrimmed(envelope.eventDate),
    startTime: toTrimmed(envelope.startTime),
    eventFormat: toTrimmed(envelope.eventFormat),
    venueName: toTrimmed(envelope.venueName),
    location: toTrimmed(envelope.location),
    price: envelope.price ?? null,
    currency: toTrimmed(envelope.currency) || 'SS',
    bookingStatus: toTrimmed(envelope.bookingStatus)?.toLowerCase() ?? null,
    bookedUsers,
    deletedAt,
    detailLine,
    subLine,
    isBooked: String(envelope.bookingStatus ?? '').toLowerCase() === 'paid',
    isDeleted: Boolean(deletedAt),
  };
};

export const isSharedEventCardMessage = (message = {}) => {
  const payload = message?.sharedEventPayload ?? normalizeSharedEventMessage(message);
  return Boolean(payload?.eventId);
};
